import { CalendarCheck, CheckCircle2, CalendarOff } from 'lucide-react';
import { Link } from 'react-router';

import { EmployeeJobCard } from '@/components/employee/EmployeeJobCard';
import { EmployeePage } from '@/components/employee/EmployeePage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { KpiCard } from '@/components/ui/kpi-card';
import { useEmployeeDashboard } from '@/hooks/employee-portal';
import { formatDay } from '@/lib/dates';
import { TimeTrackingCard } from './TimeTrackingCard';

/*
 * /employee — ROADMAP.md §6.1.
 *
 * Today's work, and only today's. The prototype's dashboard is Jobs Today,
 * Completed, and Today's Jobs in time order, and that is the right shape: a
 * cleaner opens this in the morning to find out where they are going.
 *
 * **The Time Tracking card leads the page**, which is FINDINGS.md §11.5's one
 * unambiguous compliment to the prototype: clocking in is the first thing a
 * cleaner does, so it is the first thing on the screen. It arrived in 6.5 with
 * `time_log` rather than being stubbed in 6.1 — a full-width Clock In button
 * that cannot clock anybody in is worse than a page that does not yet have one.
 *
 * Three rules from design-system.md §7 are visible on this screen, the same
 * three the client dashboard demonstrates: zero is `0`, a failed request is an
 * error rather than an empty state (P22), and an absent value is a sentence.
 */

export function EmployeeDashboardPage() {
  const dashboard = useEmployeeDashboard();
  const data = dashboard.data;

  return (
    <EmployeePage
      title={data ? `Hello, ${data.firstName}` : 'Hello'}
      description={data ? `Here is your day — ${formatDay(data.date)}.` : 'Here is your day.'}
    >
      {dashboard.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your day"
            hint="Something went wrong on our side. Try again, and ring the office if it keeps happening — do not assume you have no jobs."
            onRetry={() => {
              void dashboard.refetch();
            }}
          />
        </div>
      ) : (
        <>
          {/* First on the page, because clocking in is the first thing a
              cleaner does. A skeleton rather than an empty card while it
              loads — a Clock In button that appears and then changes to Clock
              Out is one people tap twice. */}
          {dashboard.isPending || !data ? (
            <div aria-hidden="true" className="h-[236px] animate-pulse rounded-lg bg-ink-100" />
          ) : (
            <TimeTrackingCard time={data.time} />
          )}

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <KpiCard
              label="Jobs today"
              value={data?.counts.jobsToday ?? null}
              icon={CalendarCheck}
              isLoading={dashboard.isPending}
              subLabel="Everything on your list for today"
            />
            <KpiCard
              label="Completed today"
              value={data?.counts.completedToday ?? null}
              icon={CheckCircle2}
              isLoading={dashboard.isPending}
              subLabel="Finished and signed off"
            />
          </div>

          <section className="mt-8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-h2 text-ink-900">Today&rsquo;s jobs</h2>
              <Link
                to="/employee/jobs"
                className="min-h-touch text-label text-bark-700 underline-offset-2 hover:underline"
              >
                See all my jobs
              </Link>
            </div>

            {dashboard.isPending ? (
              <ul aria-busy="true" className="space-y-3">
                <span className="sr-only">Loading today&rsquo;s jobs</span>
                {[0, 1].map((key) => (
                  <li
                    key={key}
                    aria-hidden="true"
                    className="h-[188px] animate-pulse rounded-lg bg-ink-100"
                  />
                ))}
              </ul>
            ) : data && data.todaysJobs.length > 0 ? (
              <ul className="space-y-3">
                {data.todaysJobs.map((job) => (
                  <EmployeeJobCard key={job.id} job={job} showDate={false} />
                ))}
              </ul>
            ) : (
              <div className="rounded-lg border border-ink-300 bg-white shadow-card">
                <EmptyState
                  icon={CalendarOff}
                  title="Nothing on your list today."
                  hint="If you were expecting a job, check My Schedule or ring the office — this is what an empty day looks like, not a problem loading it."
                />
              </div>
            )}
          </section>
        </>
      )}
    </EmployeePage>
  );
}
